import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { HELP_QUESTION_MAX_LENGTH, describeAnswerAssurance } from '@dha-nexus/shared';

import { PageHeader } from '../../components/primitives.jsx';
import { useIntakeDraftAutosave } from '../feedbackIntake/useIntakeDraftAutosave.js';
import '../feedbackIntake/feedbackIntake.css';
import {
  askQuestion,
  getQuestionIntake,
  newIdempotencyKey,
  patchQuestionIntake,
  prepareQuestionIntake,
  submitQuestionIntake,
} from './helpQuestionsApi.js';
import {
  describeNonVerificationProvenance,
  describeNonVerificationStatus,
} from './nonVerificationCopy.js';
import './helpQuestions.css';

// Help Q&A (typed help/intake framework, commit 5).
//
// Ask PRIME a question. PRIME answers it from its knowledge base where it can, and
// hands it to a person where it cannot.
//
// THREE THINGS THIS PAGE IS CAREFUL ABOUT
//
// 1. It never claims more than the server did. `decision.deflected` is a
//    SERVER-SIDE verdict from a deterministic policy, and this page renders it. The
//    assurance line is a SOURCE COUNT, never a score — a number like 0.71 is not
//    something a user can act on, and showing it as confidence implies a precision
//    the retrieval score does not carry. There is no client-side confidence rule
//    here, and adding one would be a defect.
//
// 2. A non-deflected answer is a beginning, not an error screen. The whole point of
//    the vertical is that the question reaches a person WITHOUT the user retyping
//    it: the title and the question are already on the intake, so the handoff only
//    asks for the context PRIME could not infer.
//
// 3. "Ask the team anyway" is always available — including on an answer PRIME was
//    confident about. A user who reads a grounded answer and still wants a human is
//    telling us something, and the governed record records which of the two happened
//    (`asked_team_anyway` vs `handoff_submitted`).

const CONTEXT_MAX_LENGTH = 4000;
const TITLE_MAX_LENGTH = 400;

/**
 * Origins a citation may link OUT to, from configuration.
 *
 * Empty by default (commit-5 audit P2-2): a knowledge-base document uri is content
 * PRIME did not author, and turning an arbitrary one into a live outbound link on a
 * DHA page is a redirect surface. An origin has to be named by an operator before it
 * becomes clickable; everything else renders as a titled reference, which is all a
 * reader needs to go and find the source.
 */
export function allowedCitationOrigins(rawValue = import.meta.env?.VITE_CITATION_LINK_ORIGINS) {
  return String(rawValue || '')
    .split(',')
    .map((entry) => entry.trim().replace(/\/+$/, ''))
    .filter(Boolean);
}

/**
 * How a citation is presented.
 *
 * A knowledge-base source uri is not necessarily something a browser can open:
 * `s3://…` is a storage location, not a link. Rendering it as an anchor would give
 * the user a link that silently does nothing, so each shape is handled explicitly
 * and an unopenable source falls back to its title and excerpt — which is still
 * everything needed to go and find it.
 *
 * `internal` requires a SINGLE leading slash. `//host/path` also starts with a slash
 * and used to pass, which made it a protocol-relative link to another origin rendered
 * as an in-app route — an off-site navigation wearing an internal link's clothes
 * (commit-5 audit P2-2). `external` additionally requires an allowlisted origin.
 */
export function citationLinkKind(uri, allowedOrigins = allowedCitationOrigins()) {
  const value = String(uri || '').trim();
  if (!value) return 'none';
  // Single leading slash only — and no reverse solidus ANYWHERE in the path.
  // Browsers treat `\` as `/` when parsing http(s) URLs, so `/\evil.example/path`
  // is the same protocol-relative off-site navigation `//evil.example/path` is
  // (re-audit finding). Backslash has no place in a canonical in-app route, so its
  // presence anywhere demotes the uri to a titled reference.
  if (/^\/(?![/\\])/.test(value) && !value.includes('\\')) return 'internal';
  if (/^https?:\/\//i.test(value)) {
    try {
      const { origin } = new URL(value);
      return allowedOrigins.includes(origin) ? 'external' : 'reference';
    } catch {
      return 'reference';
    }
  }
  return 'reference';
}

/**
 * The distinct-source count for an answer: the server-computed value when
 * present (every record since the distinct-source fix carries it), else a
 * legacy fallback that mirrors the server's canonical identity EXACTLY —
 * `normalizeSourceUri` in questionDeflection.service.js: URL-parse, drop the
 * fragment, lowercase the hostname, strip one trailing slash; non-URL values
 * just lose a trailing slash. Chunk counting is never the fallback — three
 * chunks of two documents are two sources, not three.
 */
export function deriveDistinctSourceCount(answer) {
  if (Number.isFinite(answer?.distinctSourceCount)) return answer.distinctSourceCount;
  const normalizeUri = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return null;
    try {
      const parsed = new URL(text);
      parsed.hash = '';
      parsed.hostname = parsed.hostname.toLowerCase();
      return parsed.toString().replace(/\/$/, '');
    } catch {
      return text.replace(/\/$/, '');
    }
  };
  return new Set(
    (answer?.citations || []).map((citation) => {
      const uri = normalizeUri(citation?.uri);
      if (uri) return `uri:${uri}`;
      const documentId = String(citation?.documentId || citation?.sourceDocumentId || '').trim();
      if (documentId) return `document:${documentId}`;
      return `citation:${String(citation?.evidenceId || citation?.citationId || '')}`;
    }),
  ).size;
}

function CitationList({ citations }) {
  if (!citations?.length) return null;
  const allowedOrigins = allowedCitationOrigins();
  return (
    <ol className="help-question-citations" data-testid="help-question-citations">
      {citations.map((citation) => {
        const kind = citationLinkKind(citation.uri, allowedOrigins);
        return (
          <li
            key={`${citation.marker}-${citation.citationId}`}
            data-testid="help-question-citation"
          >
            <span className="help-question-citation-marker" aria-hidden="true">
              [{citation.marker}]
            </span>
            <span className="help-question-citation-body">
              {kind === 'external' ? (
                <a
                  href={citation.uri}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="help-question-citation-link"
                >
                  {citation.title}
                </a>
              ) : kind === 'internal' ? (
                <Link to={citation.uri} data-testid="help-question-citation-link">
                  {citation.title}
                </Link>
              ) : (
                <b data-testid="help-question-citation-title">{citation.title}</b>
              )}
              {citation.excerpt ? (
                <span className="help-question-citation-excerpt">{citation.excerpt}</span>
              ) : null}
              {kind === 'reference' ? (
                <span className="help-question-citation-note">Stored source: {citation.uri}</span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function HelpQuestionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const resumeIntakeId = searchParams.get('intake');

  const [question, setQuestion] = useState('');
  const [intake, setIntake] = useState(null);
  const [turns, setTurns] = useState([]);
  const [answer, setAnswer] = useState(null);
  const [decision, setDecision] = useState(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [context, setContext] = useState('');
  const [liveNote, setLiveNote] = useState('');
  const [actionError, setActionError] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [validationKeys, setValidationKeys] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const intakeRef = useRef(null);
  // ONE key per logical ask, retained until the ask reaches a terminal state, so a
  // retry after an ambiguous failure replays the answer instead of spending a second
  // retrieval and appending a second pair of turns.
  const askKeyRef = useRef(newIdempotencyKey());
  const submitKeyRef = useRef(newIdempotencyKey());
  const questionInputRef = useRef(null);
  const answerHeadingRef = useRef(null);
  const contextInputRef = useRef(null);

  intakeRef.current = intake;

  const sourceRoute = location.state?.fromRoute || null;

  const applyIntake = useCallback((loaded) => {
    if (!loaded) return null;
    setIntake(loaded);
    intakeRef.current = loaded;
    if (Array.isArray(loaded.messages)) setTurns(loaded.messages);
    return loaded;
  }, []);

  // ONE announcement channel, so the autosave line and the search progress cannot
  // mask each other (the request page learned this the same way).
  const announce = useCallback((text) => {
    setLiveNote(text);
  }, []);

  // Autosave for the HANDOFF context only. There is nothing to save before the first
  // ask — the ask itself creates the intake — and a debounce running over an empty
  // form would create a draft the user never asked for.
  const buildPayload = useCallback(
    () => ({
      title: title.trim() || null,
      details: { description: context },
    }),
    [context, title],
  );

  const persist = useCallback(
    async (payload) => {
      const current = intakeRef.current;
      if (!current) return null;
      const response = await patchQuestionIntake(current.id, {
        expectedRevision: current.revision,
        ...payload,
      });
      return applyIntake(response?.data);
    },
    [applyIntake],
  );

  const onConflict = useCallback(async () => {
    try {
      const refreshed = await getQuestionIntake(intakeRef.current.id);
      applyIntake(refreshed?.data);
      return 'This question was changed in another tab. Your text is still here — save again to keep it.';
    } catch {
      return 'This question was changed elsewhere and PRIME could not reload it.';
    }
  }, [applyIntake]);

  const { saveState, saveMessage, setSaveMessage, markDirty, save, isDirty } =
    useIntakeDraftAutosave({
      buildPayload,
      persist,
      enabled: handoffOpen && Boolean(intake) && !submission,
      watch: [title, context],
      onConflict,
      savedMessage: 'Saved. You can close this page and come back to it.',
      savingMessage: 'Saving what you added…',
      describeError: (error) =>
        error?.message
          ? `PRIME could not save what you added: ${error.message}`
          : 'PRIME could not save what you added. Your text is still here — try again.',
    });

  // Resume a question from My Feedback.
  useEffect(() => {
    if (!resumeIntakeId || intakeRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getQuestionIntake(resumeIntakeId);
        if (cancelled) return;
        const loaded = applyIntake(response?.data);
        setTitle(loaded?.title || '');
        setContext(loaded?.details?.description || '');
        if (loaded?.status === 'submitted') {
          setSubmission({ intake: loaded });
        } else if (loaded?.deflection) {
          setDecision({
            deflected: loaded.deflection.deflected === true,
            reasons: loaded.deflection.reasons || [],
            outcome: loaded.deflection.outcome || null,
          });
          const answerTurn = (loaded.messages || [])
            .filter((message) => message.actorType === 'assistant')
            .slice(-1)[0];
          setAnswer({
            answerText: answerTurn?.content || '',
            citations: loaded.deflection.citations || [],
            // The STORED distinct-source count. Without it the page falls back
            // to counting citation chunks, and a resumed answer with three
            // chunks from two documents would claim three sources.
            distinctSourceCount: loaded.deflection.distinctSourceCount,
            generation: loaded.deflection.generation || null,
            retrieval: loaded.deflection.retrieval || null,
          });
        }
      } catch (error) {
        if (!cancelled) setLoadError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyIntake, resumeIntakeId]);

  useEffect(() => {
    if (answer) answerHeadingRef.current?.focus?.();
  }, [answer]);

  const deflected = decision?.deflected === true;
  const distinctSourceCount = deriveDistinctSourceCount(answer);
  const reasons = useMemo(() => decision?.reasons || [], [decision]);

  async function ask() {
    const text = question.trim();
    if (!text) {
      setActionError('Type your question first.');
      return;
    }
    setActionError(null);
    setBlocked(null);
    setValidationKeys([]);
    setAsking(true);
    announce('Searching PRIME knowledge…');
    try {
      const current = intakeRef.current;
      const response = await askQuestion(
        {
          intakeId: current?.id || null,
          expectedRevision: current?.revision ?? null,
          question: text,
          sourceRoute,
        },
        { idempotencyKey: askKeyRef.current },
      );
      // Terminal: the next question is a new logical operation.
      askKeyRef.current = newIdempotencyKey();
      const data = response?.data;
      const loaded = applyIntake(data?.intake);
      setTurns((existing) => {
        const known = new Set(existing.map((turn) => turn.id));
        const additions = [data.question, data.answer.message].filter(
          (turn) => turn && !known.has(turn.id),
        );
        return [...existing, ...additions];
      });
      setAnswer(data.answer);
      setDecision(data.decision);
      setQuestion('');
      setTitle((existing) => existing || loaded?.title || '');
      // The URL carries the question so a refresh resumes it rather than starting
      // over.
      if (loaded?.id && resumeIntakeId !== loaded.id) {
        setSearchParams({ intake: loaded.id }, { replace: true });
      }
      const announcedSourceCount = deriveDistinctSourceCount(data.answer);
      announce(
        data.decision.deflected
          ? `PRIME answered from ${announcedSourceCount} source${
              announcedSourceCount === 1 ? '' : 's'
            }.`
          : 'PRIME could not find a reliable answer. You can send the question to the team.',
      );
      if (!data.decision.deflected) setHandoffOpen(true);
    } catch (error) {
      if (error?.code === 'HELP_QUESTION_BLOCKED') {
        // The question never left PRIME. The typed text is kept so the user can edit
        // the offending part out rather than starting again.
        setBlocked(error.details || { sensitiveFindings: [] });
        announce(
          'PRIME did not search this question because it looks like it contains protected information.',
        );
      } else {
        setActionError(
          error?.message ||
            'PRIME could not search for an answer. Your question is still here — try again.',
        );
        announce('PRIME could not search for an answer.');
      }
    } finally {
      setAsking(false);
    }
  }

  function onQuestionKeyDown(event) {
    // Enter asks; Shift+Enter is a newline. Same contract as the request page's
    // answer box, so the two conversations behave identically.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!asking) void ask();
    }
  }

  function openHandoff() {
    setHandoffOpen(true);
    setActionError(null);
    // Prefill from what PRIME already knows, so nothing is retyped.
    setTitle((existing) => existing || intake?.title || '');
    setTimeout(() => contextInputRef.current?.focus?.(), 0);
  }

  /**
   * A save that both SUCCEEDED and left the draft CLEAN is the prerequisite.
   *
   * Two different facts, and the commit-5 audit found them conflated. `save()` returns
   * null for a failure or a revision conflict — but it returns the saved aggregate even
   * when the user typed more while the PATCH was in flight, because the PATCH itself
   * genuinely succeeded. Treating that as clean submitted the OLDER text while the newer
   * text stayed on screen, and then told the user it had been sent.
   *
   * So the gate is `saved && !isDirty()`. The hook's edit-sequence guard leaves the
   * draft dirty in exactly that case, which is the signal this reads.
   */
  const requireSavedContext = useCallback(async () => {
    const saved = await save();
    if (!saved) {
      setActionError(
        'PRIME could not save what you added, so nothing was sent. Your text is still here — try again.',
      );
      return null;
    }
    if (isDirty()) {
      setActionError(
        'You kept typing while PRIME was saving, so nothing was sent. Your latest text is still here — send again to include it.',
      );
      return null;
    }
    return saved;
  }, [isDirty, save]);

  async function askTheTeam() {
    setActionError(null);
    setValidationKeys([]);
    setBusy(true);
    try {
      const saved = await requireSavedContext();
      if (!saved) return;

      let target = saved;
      // A DEFLECTED question goes straight to submitted — the shared status graph
      // gives it exactly that one edge, and there is nothing left to prepare. A
      // question PRIME could not answer is prepared first, exactly like every other
      // typed intake.
      if (target.status !== 'deflected') {
        const prepared = await prepareQuestionIntake(target.id, target.revision);
        target = applyIntake(prepared?.data) || target;
      }
      const submitted = await submitQuestionIntake(target.id, target.revision, {
        idempotencyKey: submitKeyRef.current,
      });
      submitKeyRef.current = newIdempotencyKey();
      applyIntake(submitted?.data?.intake);
      setSubmission(submitted?.data || null);
      setSaveMessage('');
      announce('Your question was sent to the team.');
    } catch (error) {
      if (error?.details?.reason === 'intake_not_ready') {
        setValidationKeys(error.details.missing || []);
        setActionError('PRIME needs a little more before it can send this to the team.');
      } else {
        setActionError(
          error?.message ||
            'PRIME could not send your question to the team. Your text is still here — try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  function askAnother() {
    // A new question is a NEW intake: a deflected question's stored decision, answer,
    // and transcript are the record of ONE answered question, and the server refuses
    // to overwrite them.
    setIntake(null);
    intakeRef.current = null;
    setTurns([]);
    setAnswer(null);
    setDecision(null);
    setSubmission(null);
    setHandoffOpen(false);
    setTitle('');
    setContext('');
    setQuestion('');
    setActionError(null);
    setBlocked(null);
    setValidationKeys([]);
    askKeyRef.current = newIdempotencyKey();
    submitKeyRef.current = newIdempotencyKey();
    setSearchParams({}, { replace: true });
    announce('Ask another question.');
    setTimeout(() => questionInputRef.current?.focus?.(), 0);
  }

  if (loadError) {
    return (
      <section className="support-ticket-page" data-testid="help-questions-page">
        <PageHeader eyebrow="Help" title="Ask a question" />
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="help-question-load-error"
        >
          <h3>PRIME could not open this question</h3>
          <p>{loadError.message || 'The question could not be loaded.'}</p>
          <p>
            <button type="button" className="btn ghost sm" onClick={askAnother}>
              Ask a new question
            </button>{' '}
            or <Link to="/feedback">go to My Feedback</Link>.
          </p>
        </div>
      </section>
    );
  }

  if (submission) {
    const outcome = submission.intake?.deflection?.outcome || null;
    return (
      <section className="support-ticket-page" data-testid="help-questions-page">
        <PageHeader
          eyebrow="Help"
          title="Ask a question"
          subtitle="Your question is with the team."
        />
        <p
          className="support-ticket-savestate"
          aria-live="polite"
          data-testid="help-question-live-note"
        >
          {liveNote}
        </p>
        <div className="support-ticket-card" data-testid="help-question-submitted">
          <h2>Sent to the team</h2>
          <p className="support-ticket-card-hint">
            {outcome === 'asked_team_anyway'
              ? 'PRIME had answered this question, and you asked the team as well. Reviewers receive your question, the search terms and sources PRIME used, and how PRIME handled it — not the answer text.'
              : 'PRIME could not answer this one, so a person will. They can see what you asked and what PRIME searched for.'}
          </p>
          <p className="support-ticket-card-hint">
            Track it in <Link to="/feedback">My Feedback</Link>.
          </p>
          <button
            type="button"
            className="btn primary"
            onClick={askAnother}
            data-testid="help-question-ask-another"
          >
            Ask another question
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="support-ticket-page help-questions-page" data-testid="help-questions-page">
      <PageHeader
        eyebrow="Help"
        title="Ask a question"
        subtitle="PRIME answers from its own knowledge base where it can, and sends your question to a person where it cannot."
      />

      {/* One polite live region for search progress, save state, and outcomes. */}
      <p
        className={`support-ticket-savestate${saveState === 'failed' ? ' is-failed' : ''}`}
        aria-live="polite"
        data-testid="help-question-live-note"
      >
        {saveMessage || liveNote}
      </p>

      {actionError ? (
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="help-question-error"
        >
          <h3>Something went wrong</h3>
          <p>{actionError}</p>
        </div>
      ) : null}

      {blocked ? (
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="help-question-blocked"
        >
          <h3>PRIME did not search this question</h3>
          <p>
            It looks like it contains protected information, so PRIME did not send it to the
            knowledge base or to the AI assistant. Take that part out and ask again.
          </p>
          <ul>
            {(blocked.sensitiveFindings || []).map((finding, index) => (
              <li key={`${finding.detector}-${index}`} data-testid="help-question-blocked-finding">
                {finding.message || finding.detector}{' '}
                <span className="support-ticket-finding-evidence">{finding.redactedEvidence}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {validationKeys.length ? (
        <div
          className="support-ticket-alert tone-danger"
          role="alert"
          data-testid="help-question-validation"
        >
          <h3>
            {validationKeys.length} thing{validationKeys.length === 1 ? '' : 's'} still needed
          </h3>
          <ul>
            {validationKeys.map((key) => (
              <li key={key}>{key === 'title' ? 'A short summary of your question' : key}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="support-ticket-card">
        <h2>What would you like to know?</h2>
        <div className="form-field">
          <label className="form-label" htmlFor="help-question-input">
            Your question
          </label>
          <div className="form-control">
            <textarea
              className="form-input form-textarea"
              id="help-question-input"
              name="question"
              rows={3}
              ref={questionInputRef}
              value={question}
              maxLength={HELP_QUESTION_MAX_LENGTH}
              aria-describedby="help-question-input-hint"
              onChange={(event) => setQuestion(event.target.value)}
              onKeyDown={onQuestionKeyDown}
            />
          </div>
          <div className="form-hint" id="help-question-input-hint">
            Plain words are fine. Press Enter to ask; Shift+Enter starts a new line.
          </div>
        </div>
        <div className="support-ticket-actions">
          <button
            type="button"
            className="btn primary"
            data-testid="help-question-ask"
            disabled={asking || !question.trim()}
            onClick={() => void ask()}
          >
            {asking ? 'Searching PRIME knowledge…' : 'Ask PRIME'}
          </button>
          {intake ? (
            <button
              type="button"
              className="btn ghost sm"
              data-testid="help-question-ask-another"
              onClick={askAnother}
            >
              Start a new question
            </button>
          ) : null}
        </div>
      </div>

      {turns.length ? (
        <div className="support-ticket-card help-question-transcript-card">
          <h2>Your conversation with PRIME</h2>
          <ol className="request-transcript" data-testid="help-question-transcript">
            {turns.map((turn) => (
              <li
                key={turn.id}
                className={`request-turn ${turn.actorType === 'user' ? 'is-user' : 'is-assistant'}`}
                data-testid={`help-question-turn-${turn.actorType}`}
              >
                <p className="request-turn-meta">
                  <b>{turn.actorType === 'user' ? 'You' : 'PRIME'}</b>
                </p>
                <p className="request-turn-body">{turn.content}</p>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {answer ? (
        <div
          className="support-ticket-card help-question-answer"
          data-testid="help-question-answer"
        >
          <h2 tabIndex={-1} ref={answerHeadingRef}>
            {deflected ? 'PRIME’s answer' : 'PRIME could not answer this one'}
          </h2>

          {deflected ? (
            // The assurance indicator. A SOURCE COUNT in plain language — never a
            // score, and never a percentage PRIME cannot justify.
            <p
              className="help-question-assurance"
              data-testid="help-question-assurance"
              data-glyph="✓"
            >
              {describeAnswerAssurance(distinctSourceCount)} Check the sources below before you rely
              on it.
            </p>
          ) : (
            // `tone-warn`, not `tone-warning`. feedbackIntake.css defines
            // tone-danger / tone-warn / tone-info / tone-success; `tone-warning`
            // matched nothing, so the one panel that has to read as "this is not
            // verified PRIME guidance" rendered as an unstyled box with the default
            // grey border and no tint — visually indistinguishable from body copy
            // (typed-intake commit 7 visual audit).
            <div data-testid="help-question-status-block">
              <div
                className="support-ticket-alert tone-warn"
                role="note"
                data-testid="help-question-not-deflected"
              >
                <p>{describeNonVerificationStatus(reasons)}</p>
              </div>
            </div>
          )}

          {!deflected ? (
            <div data-testid="help-question-provenance-block">
              <p className="support-ticket-card-hint" data-testid="help-question-provenance">
                {describeNonVerificationProvenance(answer, reasons)}
                {!answer.citations?.length
                  ? ' PRIME has no source material to show for this question.'
                  : ''}
              </p>
            </div>
          ) : null}

          <p className="help-question-answer-body" data-testid="help-question-answer-body">
            {answer.answerText}
          </p>

          {answer.citations?.length ? (
            <>
              <h3>Sources</h3>
              <CitationList citations={answer.citations} />
            </>
          ) : deflected ? (
            <p className="support-ticket-card-hint" data-testid="help-question-no-sources">
              PRIME has no source material to show for this question.
            </p>
          ) : null}

          {/* PERSISTENT, even on a deflected answer. */}
          {!handoffOpen ? (
            <div className="support-ticket-actions">
              <button
                type="button"
                className={deflected ? 'btn ghost' : 'btn primary'}
                data-testid="help-question-ask-team"
                onClick={openHandoff}
              >
                {deflected ? 'Ask the team anyway' : 'Ask the team'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {handoffOpen && intake ? (
        <div data-testid="help-question-next-step-block">
          <div className="support-ticket-card" data-testid="help-question-handoff">
            <h2>Send this question to the team</h2>
            <p className="support-ticket-card-hint">
              Your question is already attached, along with what PRIME searched for and the sources
              it found. Add anything that would help a person answer it.
            </p>

            <div className="form-field">
              <label className="form-label" htmlFor="help-question-title">
                Short summary
              </label>
              <div className="form-control">
                <input
                  className="form-input"
                  id="help-question-title"
                  name="title"
                  type="text"
                  value={title}
                  maxLength={TITLE_MAX_LENGTH}
                  // Locked while the submission is in flight: the payload has already
                  // been snapshotted, and an edit that cannot reach it must not look
                  // like one that did (commit-5 audit P1-4).
                  disabled={busy}
                  onChange={(event) => {
                    markDirty();
                    setTitle(event.target.value);
                  }}
                />
              </div>
              <div className="form-hint">Prefilled from your question — change it if you like.</div>
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="help-question-context">
                Anything else the team should know (optional)
              </label>
              <div className="form-control">
                <textarea
                  className="form-input form-textarea"
                  id="help-question-context"
                  name="context"
                  rows={4}
                  ref={contextInputRef}
                  value={context}
                  maxLength={CONTEXT_MAX_LENGTH}
                  disabled={busy}
                  onChange={(event) => {
                    markDirty();
                    setContext(event.target.value);
                  }}
                />
              </div>
              <div className="form-hint">
                What you were trying to do, or which screen you were on.
              </div>
            </div>

            <div className="support-ticket-actions">
              <button
                type="button"
                className="btn primary"
                data-testid="help-question-submit"
                disabled={busy}
                onClick={() => void askTheTeam()}
              >
                {busy ? 'Sending…' : 'Send to the team'}
              </button>
              <button
                type="button"
                className="btn ghost sm"
                data-testid="help-question-handoff-cancel"
                disabled={busy}
                onClick={() => setHandoffOpen(false)}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <p className="support-ticket-card-hint">
        Looking for something else? <Link to="/feedback/support/new">Report a problem</Link>,{' '}
        <Link to="/feedback/request/new">request a feature or service</Link>, or{' '}
        <Link to="/help">go back to the Help Center</Link>.
      </p>
    </section>
  );
}

export default HelpQuestionsPage;
